import { useImperativeHandle, useRef, type Ref } from 'react'
import { Search, X } from 'lucide-react'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '../ui/input-group'
import { clearSearchLabel } from '@/lib/commonStrings'
import { cn } from '@/lib/utils'

// The Search tab's search box. The clear button or Escape empties it.
export function SearchField({
  value,
  onValueChange,
  placeholder,
  className,
  ref,
}: {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  className?: string
  ref?: Ref<HTMLInputElement | null>
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current, [])

  return (
    <InputGroup className={cn('h-9 flex-1', className)}>
      <InputGroupAddon>
        <Search className="size-4 text-muted-foreground" />
      </InputGroupAddon>
      <InputGroupInput
        ref={inputRef}
        type="search"
        enterKeyHint="search"
        value={value}
        onChange={e => onValueChange(e.target.value)}
        onKeyDown={e => e.key === 'Escape' && onValueChange('')}
        placeholder={placeholder}
        aria-label={placeholder}
        className="[&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            onClick={() => { onValueChange(''); inputRef.current?.focus() }}
            aria-label={clearSearchLabel()}
          >
            <X />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}
